import { useEffect, useRef, useState } from 'react';

/**
 * Observes when an element enters the viewport (fires once).
 * @param {{ threshold?: number, rootMargin?: string, enabled?: boolean }} options
 * @returns {[import('react').RefObject<HTMLElement>, boolean]}
 */
export const useInView = (options = {}) => {
  const {
    threshold = 0.15,
    rootMargin = '0px 0px -40px 0px',
    enabled = true,
  } = options;

  const ref = useRef(null);
  const [isInView, setIsInView] = useState(!enabled);

  useEffect(() => {
    if (!enabled) {
      setIsInView(true);
      return undefined;
    }

    const element = ref.current;
    if (!element) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [enabled, threshold, rootMargin]);

  return [ref, isInView];
};
